<script setup>
import { useDetailsReport } from '#/detailsReport/useDetailsReport'
import { useFilters } from '#/general/useFilters'
import LockableBox from '#/general/LockableBox'
import StepProgressLine from '#/general/StepProgressLine'
import { computed, inject } from 'vue'
import PotentialScoreChart from '#/detailsReport/PotentialScoreChart'

const { global, pages } = inject('Shared')
const { potential } = useDetailsReport()
const { prettifyNumber } = useFilters()
const volume = computed(() => {
  return potential.value.parcel.currentVolume ? potential.value.parcel.currentVolume : 524
})
</script>

<template lang="pug">
.leadScoringContainer
  .boxContainer
    lockable-box(
      :main-title="global.potentialVolumeScoreLabel"
      :locked="false"
    )
      template(#subtitle)
        div(v-html="pages.detailReport.potentialAnalysis.progressLineBoxSubtitle")
      .row.full-width
        .col-lg-6.col-md-12.col-sm-12.col-xs-12.flex.flex-center
          div(style="width: 150px; height: 150px; position: relative;" v-if="potential.analysis")
            potential-score-chart(:data="potential.analysis.score" width="150px" height="150px")
            .titleContainer
              template(v-if="potential.analysis && potential.analysis.score")
                .titleOne {{ prettifyNumber(potential.analysis.score, 0) }}
                .titleTwo /100
              .titleOne(v-else) ?
          .helperContainer.potentialVolumeHelperContainer.gt-md
            .helperSign
              .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundLow}`")
              .label tief
            .helperSign
              .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundMedium}`")
              .label mittel
            .helperSign
              .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundHigh}`")
              .label hoch
        .col-lg-6.col-md-12.col-sm-12.col-xs-12.flex.flex-center
          .gt-md
            .circleContainer
              .circle(:style="potential.analysis && potential.analysis.isBuildingZoneBased ? 'background: #5FEFC0' : 'background: #F6D156'")
              .circleTitle Berechnungsmethode
            .blueSeparator
            .explanationText Ein grüner Punkt signalisiert die Berechnung anhand kommunaler Baureglementsdaten und ist entsprechend präzise/genau. Ein gelber Punkt signalisiert die Berechnung anhand der Bebauungsdichte in der direkten Umgebung bzw. Nachbarschaft.
          .lt-lg
            .flex.flex-center
              .helperContainer
                .helperSign
                  .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundLow}`")
                  .label tief
                .helperSign
                  .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundMedium}`")
                  .label mittel
                .helperSign
                  .circle(:style="`background: ${pages.detailReport.potentialAnalysis.progressLineBackgroundHigh}`")
                  .label hoch
            .blueSeparator
            .explanationText Ein grüner Punkt signalisiert die Berechnung anhand kommunaler Baureglementsdaten und ist entsprechend präzise/genau. Ein gelber Punkt signalisiert die Berechnung anhand der Bebauungsdichte in der direkten Umgebung bzw. Nachbarschaft.
            .circleContainer
              .circle(:style="potential.analysis && potential.analysis.isBuildingZoneBased ? 'background: #5FEFC0' : 'background: #F6D156'")
              .circleTitle Berechnungsmethode

  .boxContainer
    lockable-box(
      :main-title="global.renovationPressureLabel + ':'"
      :locked="false"
    )
      template(#subtitle)
        div(v-html="pages.detailReport.potentialAnalysis.renovationPressureBoxSubtitle")
      .renovationPressureScoreContainer.row
        .col-lg-6.col-md-12.col-sm-12.col-xs-12.row
          .progressLineStepperContainer
            .titleRow
              .titleOne {{ prettifyNumber(potential.parcel.renovationPressure * 100, 0) }}
              .titleTwo /100
            step-progress-line(:percentage="potential.parcel.renovationPressure * 100")
        .col-lg-6.col-md-12.col-sm-12.col-xs-12.flex.flex-center
          .helperContainer
            .helperSign
              .circle(:style="`background: ${pages.detailReport.potentialAnalysis.renovationPressureBackgroundHigh}`")
              .label hoch
            .helperSign
              .circle(:style="`background: ${pages.detailReport.potentialAnalysis.renovationPressureBackgroundMedium}`")
              .label mittel
            .helperSign
              .circle(:style="`background: ${pages.detailReport.potentialAnalysis.renovationPressureBackgroundLow}`")
              .label tief
</template>

<style scoped lang="sass">
.leadScoringContainer
  display: flex
  flex-direction: row
  width: 100%
  justify-content: space-between
  flex-wrap: wrap
  .explanationText
    margin-left: 6px
    @media screen and (min-width: 1440px)
      font-weight: 400
      font-size: 10px
      line-height: 150%
      color: #2F3849
    @media screen and (max-width: 1440px)
      font-weight: 400
      font-size: 10px
      line-height: 150%
      color: #2F3849
      margin: 10px 0 20px 0
  .blueSeparator
    border: 1px solid #DCE7F2
    width: 100%
    margin: 10px 0
  .renovationPressureScoreContainer.row
    width: 100%
    height: 100%
    padding-bottom: 16px
  .progressLineStepperContainer
    margin-top: 45px
    @media screen and (max-width: 1440px)
      width: 100%
    .titleRow
      display: flex
      flex-direction: row
      margin-bottom: 16px
  .potentialVolumeHelperContainer
    flex-direction: row !important
    .helperSign
      margin-right: 20px !important
  .helperContainer
    display: flex
    @media screen and (min-width: 1440px)
      flex-direction: column
    @media screen and (max-width: 1440px)
      flex-direction: row
      margin: 20px 0 10px
  .circleContainer
    display: flex
    flex-direction: row
    align-items: center
    margin-left: 16px
    @media screen and (max-width: 1440px)
      width: 100%
      justify-content: center
      margin-left: 0
      margin-top: 20px
      margin-bottom: 10px
    .circleTitle
      @media screen and (min-width: 1440px)
        font-weight: 600
        font-size: 14px
        line-height: 200%
        color: #626C85
      @media screen and (max-width: 1440px)
        font-weight: 600
        font-size: 14px
        line-height: 200%
        text-align: center
        color: #626C85
  .circle
    width: 16px
    height: 16px
    border-radius: 100%
    margin-right: 6px
  .boxContainer
    margin-bottom: 20px
    @media screen and (min-width: 1440px)
      width: calc(50% - 10px)
    @media screen and (max-width: 1440px)
      width: 100%
    .titleContainer
      width: 100%
      height: 100%
      display: flex
      justify-content: center
      align-items: center
      flex-direction: row
      position: absolute
      top: 0px
      left: 0px
    .titleOne
      @media screen and (min-width: 1440px)
        font-weight: 600
        font-size: 30px
        line-height: 30px
        color: #22223C
      @media screen and (max-width: 1440px)
        font-weight: 600
        font-size: 30px
        line-height: 30px
        color: #22223C
        margin-right: 5px
    .titleTwo
      font-weight: 600
      font-size: 16px
      line-height: 30px
      color: #22223C
    .helperSign
      display: flex
      flex-direction: row
      align-items: center
      margin-bottom: 12px
      @media screen and (min-width: 1440px)
      @media screen and (max-width: 1440px)
        margin-right: 15px
      .label
        font-weight: 500
        font-size: 14px
        line-height: 130%
        color: #626C85
</style>
